"use client";

import { useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import gsap from "gsap";
import { safeHref } from "@/lib/safe-href";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { useReducedMotion } from "./useReducedMotion";
import type { HeroContent } from "./types";

export function MinimalHero({ content }: { content: HeroContent }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const wordsContainerRef = useRef<HTMLSpanElement | null>(null);
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const subRef = useRef<HTMLParagraphElement | null>(null);
  const ctasRef = useRef<HTMLDivElement | null>(null);
  const ruleRef = useRef<HTMLSpanElement | null>(null);
  const reduced = useReducedMotion();

  const words = useMemo(
    () => content.title.split(/\s+/).filter(Boolean),
    [content.title]
  );

  useEffect(() => {
    if (!rootRef.current) return;
    const ctx = gsap.context(() => {
      const targets = wordsContainerRef.current
        ? Array.from(
            wordsContainerRef.current.querySelectorAll<HTMLSpanElement>(
              "[data-hero-word]"
            )
          )
        : [];
      const intro = [
        badgeRef.current,
        subRef.current,
        ctasRef.current,
        ruleRef.current,
      ].filter(Boolean);

      if (reduced) {
        gsap.set([...targets, ...intro], { opacity: 1, y: 0 });
        if (ruleRef.current) gsap.set(ruleRef.current, { scaleX: 1 });
        return;
      }

      gsap.set(targets, { yPercent: 110, opacity: 0 });
      gsap.set(intro, { opacity: 0, y: 20 });
      if (ruleRef.current)
        gsap.set(ruleRef.current, { scaleX: 0, transformOrigin: "left center" });

      const tl = gsap.timeline({ delay: 0.1 });
      tl.to(badgeRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
      });
      tl.to(
        targets,
        {
          yPercent: 0,
          opacity: 1,
          duration: 0.85,
          ease: "expo.out",
          stagger: 0.08,
        },
        "-=0.2"
      );
      if (ruleRef.current) {
        tl.to(
          ruleRef.current,
          { scaleX: 1, duration: 0.7, ease: "power3.out" },
          "-=0.5"
        );
      }
      tl.to(
        subRef.current,
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
        "-=0.3"
      );
      tl.to(
        ctasRef.current,
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
        "-=0.25"
      );
    }, rootRef);
    return () => ctx.revert();
  }, [reduced, content.title]);

  return (
    <section
      ref={rootRef}
      className="hero-section relative isolate overflow-hidden bg-[var(--background)] px-4 py-20 text-[var(--foreground)] md:py-28"
      aria-label="Hero"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative z-10 mx-auto max-w-5xl">
        {content.badge ? (
          <span
            ref={badgeRef}
            className="mb-6 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.32em] text-[var(--accent)]"
          >
            <span aria-hidden className="inline-block h-px w-8 bg-[var(--accent)]" />
            {content.badge}
          </span>
        ) : null}

        <h1 className="text-balance text-5xl font-extrabold leading-[1.02] tracking-tight text-[var(--primary-darker)] md:text-7xl lg:text-[5.5rem] lg:leading-[1.01]">
          <span ref={wordsContainerRef} aria-hidden className="block">
            {words.map((w, i) => (
              <span
                key={`${w}-${i}`}
                className="inline-block overflow-hidden align-bottom"
              >
                <span
                  data-hero-word
                  className="inline-block will-change-transform"
                >
                  {w}
                  {i < words.length - 1 ? " " : ""}
                </span>
              </span>
            ))}
          </span>
          <span className="sr-only">{content.title}</span>
        </h1>

        <span
          ref={ruleRef}
          aria-hidden
          className="mt-8 block h-px w-32 origin-left bg-[var(--accent)]"
        />

        {content.subtitle ? (
          <p
            ref={subRef}
            className="mt-8 max-w-2xl text-pretty text-base leading-relaxed text-[var(--muted-foreground)] md:text-lg"
          >
            {content.subtitle}
          </p>
        ) : null}

        {(content.primaryCta || content.secondaryCta) && (
          <div ref={ctasRef} className="mt-10 flex flex-col gap-3 sm:flex-row">
            {content.primaryCta ? (
              <Link
                href={safeHref(content.primaryCta.href)}
                className={cn(
                  buttonVariants({ variant: "primary", size: "lg" }),
                  "focus-visible:ring-offset-white"
                )}
              >
                {content.primaryCta.label}
              </Link>
            ) : null}
            {content.secondaryCta ? (
              <Link
                href={safeHref(content.secondaryCta.href)}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-300 px-8 text-sm font-semibold text-[var(--primary-darker)] transition hover:border-slate-900 hover:bg-slate-900 hover:text-white"
              >
                {content.secondaryCta.label}
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
